import { useCallback, useEffect, useState } from 'react'

interface PropsIn<T> {
  delay?: number
  initialValue?: T
  onChange?: (value: T) => void
}

interface PropsOut<T> {
  debouncedValue: T
  pendingValue: T
  setValue: (value: T) => void
  setImmediateValue: (value: T) => void
}

export function useDebouncedValue<T>({ delay, initialValue, onChange }: PropsIn<T>): PropsOut<T> {
  const [debouncedValue, setValueToDebounce] = useState<T>(initialValue)
  const [pendingValue, setPendingValue] = useState<T>(initialValue)

  useEffect(() => {
    // Update debounced value after delay
    const handler = setTimeout(() => {
      if (debouncedValue !== pendingValue) {
        setValueToDebounce(pendingValue)
        onChange?.(pendingValue)
      }
    }, delay)
    return () => {
      clearTimeout(handler)
    }
  }, [pendingValue, delay])

  const setValue = useCallback(
    (newValue: T) => {
      setPendingValue(newValue)
    },
    [setPendingValue]
  )

  const setImmediateValue = useCallback(
    (newValue: T) => {
      setValueToDebounce(newValue)
      setPendingValue(newValue)
      onChange?.(newValue)
    },
    [onChange]
  )

  return {
    debouncedValue,
    pendingValue,
    setValue,
    setImmediateValue
  }
}
